// ASK: multis for what the punter actually asked for.
//
// Every other printer in this app answers one question — "what does strategy X
// make of today's board?" — and the punter's only way in was to pick which
// strategies ran. On Grand Final day (2026-09-26) that meant no ticket on the
// pad carried the Grand Final, no way to ask for one, and no "another one" that
// was not a re-roll of every strategy at once (docs/tips-root-causes.md).
//
// This is one pure function over the boards the device already holds:
//
//   answer(boards, request, { asOf, count, seed }) → { results, unsatisfied, … }
//
// THE REQUEST IS PREFERENCES, NOT ONLY FILTERS. Every dimension that names a
// thing — a sport, an event, a book — takes a weight:
//
//   must    at least one leg is this. Never violated.
//   only    every leg is this. Never violated.
//   prefer  a ranking weight. The multi may still pull from elsewhere when that
//           makes a better or more complete ticket, and says so (`honoured`).
//   avoid   never. A hard exclude.
//
// WHAT A MULTI IS WORTH. A leg's value is its price against the MARKET's fair
// chance — the consensus of every other book on the board with this book's own
// vote left out (`consensus` in universe.js), the same yardstick Market edge
// uses. A multi is priced at ONE book, because three books' best prices are a
// combined price no counter will write (engine review #4). Its log value is the
// sum of its legs' log values, so the search is over which legs, per book, not
// over arithmetic; the only thing that couples legs is the risk band, which is
// why this is a beam search and not a sort.
//
// DETERMINISTIC. Nothing reads the clock (`asOf` is an argument); every sort has
// an explicit tie-break; the seed only breaks ties. "Another one" is the same
// request with the multis already shown in `excludeMultis`, never a re-roll.

import { fnv1a } from './hash.js';
import { NOT_A_BET_BOOK, consensus, bookFair } from './universe.js';
import { shrinkToward } from './combine.js';
import { describe } from './strategy.js';
import { RACING_LEAD_MS } from './strategies/racing.js';
import { isRacingSport, sportLabel, teamId } from './teams.js';
import { TIER_BANDS } from '../tier.js';
import { legLink } from '../bookmakers.js';

export const ASK_VERSION = 1;
export const ASK_STRATEGY_ID = 'ask';

export const WEIGHTS = ['must', 'only', 'prefer', 'avoid'];
export const RISKS = ['low', 'medium', 'high', 'outrageous'];
export const STRATEGIES = ['best', 'edge', 'likely', 'longshots'];
export const WHENS = ['today', 'now', 'tonight', 'tomorrow', 'this-weekend'];

/** Leg counts a risk implies when the punter has not named one. */
export const LEGS_FOR_RISK = {
  low: { min: 2, max: 3 },
  medium: { min: 3, max: 4 },
  high: { min: 4, max: 6 },
  outrageous: { min: 5, max: 8 },
};
export const DEFAULT_LEGS = { min: 2, max: 4 };
export const MAX_LEGS = 10;

/** The combined-chance band a risk means — the same bands `tierFor` labels with. */
export const RISK_BANDS = {
  low: [TIER_BANDS.low[0], 1],
  medium: TIER_BANDS.medium,
  high: TIER_BANDS.high,
  outrageous: [0.002, TIER_BANDS.outrageous[1]],
};

/** How soon before the start a leg stops being offered. Races have their own. */
export const SPORT_LEAD_MS = 5 * 60_000;

/** What a preferred leg is worth in the ranking, in log-value units (≈ 4% value). */
export const PREFER_BONUS = 0.04;

/** The runners of a race worth offering as a leg: its likeliest few. */
export const RUNNERS_PER_RACE = 4;

/** Legs no sensible multi is built from. */
export const PRICE_RANGE = [1.04, 51];

const TZ = 'Australia/Sydney';
// The search's breadth. A band (a risk or an odds range) couples the legs and
// needs the wider beam to reach it; with no band a multi's score is a plain sum
// and the best sets are found from far fewer partial ones.
const BEAM = 120;
const BEAM_FREE = 50;
const BEAM_BAND = 60;
const POOL = 48;
const COMPLETES_PER_BOOK = 4000;
/** How many of each book's best multis are kept for ranking: a page and its variety. */
export const KEEP = 120;
const TIE = (seed, key) => fnv1a(`${seed}|${key}`);

// --- the request ----------------------------------------------------------------

const asWeighted = (list, field) => (Array.isArray(list) ? list : [])
  .map((x) => (typeof x === 'string' ? { [field]: x, weight: 'must' } : x))
  .filter((x) => x && typeof x[field] === 'string' && WEIGHTS.includes(x.weight ?? 'must'))
  .map((x) => ({
    [field]: x[field],
    weight: x.weight ?? 'must',
    // An event may carry what the chip that named it knew: its name and start.
    // A match that has started is gone from the board, and without these the
    // answer could only say "that event is not on the board".
    ...(typeof x.label === 'string' ? { label: x.label } : {}),
    ...(typeof x.kickoff === 'string' ? { kickoff: x.kickoff } : {}),
  }));

function asLegs(legs, risk) {
  if (Number.isInteger(legs)) return { min: clampLegs(legs), max: clampLegs(legs) };
  if (legs && (Number.isInteger(legs.min) || Number.isInteger(legs.max))) {
    const min = clampLegs(legs.min ?? legs.max);
    const max = clampLegs(legs.max ?? legs.min);
    return { min: Math.min(min, max), max: Math.max(min, max) };
  }
  return { ...(risk ? LEGS_FOR_RISK[risk] : DEFAULT_LEGS) };
}
const clampLegs = (n) => Math.max(1, Math.min(MAX_LEGS, Number(n) || 1));

/** The longest search a punter types into a phone: a team, a runner, a venue. */
export const MAX_SEARCH = 40;
const asSearch = (q) => (typeof q === 'string' ? q.trim().toLowerCase().slice(0, MAX_SEARCH).trim() : '');

/**
 * Any request, however partial, as the whole thing — every field present, every
 * weight valid, nothing the engine has to second-guess. An empty request is the
 * default: today, two to four legs, ranked by value.
 */
export function normaliseRequest(raw = {}) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const risk = RISKS.includes(r.risk) ? r.risk : null;
  const odds = r.odds && (Number.isFinite(r.odds.min) || Number.isFinite(r.odds.max))
    ? { min: Number.isFinite(r.odds.min) ? r.odds.min : 1, max: Number.isFinite(r.odds.max) ? r.odds.max : Infinity }
    : null;
  const when = WHENS.includes(r.when) || (r.when && r.when.from && r.when.to) ? r.when : 'today';
  const include = [...new Set((r.include ?? []).filter((k) => typeof k === 'string'))].sort();
  const legs = asLegs(r.legs, risk);
  // Legs the punter locked are part of the count: lock three and ask for "any
  // legs" and the multi is three or more, never two.
  if (include.length > legs.max) legs.max = Math.min(MAX_LEGS, include.length);
  if (include.length > legs.min) legs.min = Math.min(legs.max, include.length);
  return {
    sports: asWeighted(r.sports, 'sport'),
    events: asWeighted(r.events, 'event'),
    bookmakers: asWeighted(r.bookmakers, 'bookmaker'),
    when,
    legs,
    risk: odds ? null : risk,
    odds,
    strategy: STRATEGIES.includes(r.strategy) ? r.strategy : 'best',
    // A few letters of a team, runner, match, venue or sport: every multi then
    // carries at least one leg that says them.
    search: asSearch(r.search),
    include,
    exclude: [...new Set((r.exclude ?? []).filter((k) => typeof k === 'string'))].sort(),
    excludeMultis: [...new Set((r.excludeMultis ?? []).filter((k) => typeof k === 'string'))].sort(),
    seed: Number.isInteger(r.seed) ? r.seed : 0,
  };
}

/** A stable identity for a request, so analytics and history can group them. */
export function requestKey(request) {
  const r = normaliseRequest(request);
  const { excludeMultis: _shown, seed: _seed, search, ...shape } = r;
  // An empty search leaves the key as it was before search existed, so the
  // requests history and analytics already hold still group with new ones.
  if (search) shape.search = search;
  return fnv1a(JSON.stringify(shape)).toString(16);
}

// --- what a sport id matches ------------------------------------------------------

/**
 * A sport as the punter names it: a board id ("afl"), a racing code
 * ("racing_horse"), or a group — "racing" (every code) or "sports" (everything
 * that is not a race).
 */
export function sportMatches(id, sport) {
  if (id === sport) return true;
  if (id === 'racing') return isRacingSport(sport);
  if (id === 'sports') return !isRacingSport(sport);
  return false;
}

export function sportName(id) {
  if (id === 'racing') return 'racing';
  if (id === 'sports') return 'sport';
  return sportLabel(id);
}

// --- the window -------------------------------------------------------------------

/** Milliseconds `tz` is ahead of UTC at `ms`. */
const FORMATTERS = new Map();
function tzOffset(ms, tz) {
  if (!FORMATTERS.has(tz)) {
    FORMATTERS.set(tz, new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }));
  }
  const parts = Object.fromEntries(FORMATTERS.get(tz).formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** The instant a wall-clock time on `date` happens in `tz`. */
export function zoned(date, hour, tz = TZ) {
  const [y, m, d] = date.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, hour);
  return guess - tzOffset(guess - tzOffset(guess, tz), tz);
}

const dateIn = (ms, tz = TZ) => new Date(ms).toLocaleDateString('en-CA', { timeZone: tz });
const addDays = (date, n) => new Date(Date.parse(`${date}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/**
 * `[from, to)` in ms for a `when`, on the board's own date.
 *
 * "Tonight" is 5 pm to 5 am, which is when a punter in Sydney says tonight; "now"
 * is the next three hours; "this weekend" is Saturday and Sunday of the week
 * the board is in (the board itself only ever holds today and tomorrow).
 */
export function windowFor(when, { forDate, asOf, tz = TZ }) {
  const asOfMs = Date.parse(asOf);
  const date = forDate ?? dateIn(asOfMs, tz);
  if (when && typeof when === 'object') return [Date.parse(when.from), Date.parse(when.to)];
  switch (when) {
    case 'now': return [asOfMs, asOfMs + 3 * 3_600_000];
    case 'tonight': return [zoned(date, 17, tz), zoned(addDays(date, 1), 5, tz)];
    case 'tomorrow': return [zoned(addDays(date, 1), 0, tz), zoned(addDays(date, 2), 0, tz)];
    case 'this-weekend': {
      const dow = new Date(`${date}T12:00:00Z`).getUTCDay();
      const sat = addDays(date, dow === 0 ? -1 : 6 - dow);
      return [Math.max(zoned(sat, 0, tz), zoned(date, 0, tz)), zoned(addDays(sat, 2), 0, tz)];
    }
    default: return [zoned(date, 0, tz), zoned(addDays(date, 1), 0, tz)];
  }
}

export function whenPhrase(when) {
  if (when && typeof when === 'object') return 'in that window';
  return {
    now: 'in the next three hours', tonight: 'tonight', tomorrow: 'tomorrow', 'this-weekend': 'this weekend',
  }[when] ?? 'today';
}

// --- events: what the punter calls them --------------------------------------------

/**
 * What a game is called: "Home v Away", or a race's venue and number. Nothing
 * is special-cased — a grand final and a Tuesday-night fixture are both games
 * on the board, and the punter says which one they want.
 */
export function eventLabel(match) {
  if (match.runners) return match.raceLabel ?? `${match.venue} R${match.raceNumber}`;
  return `${match.home?.name} v ${match.away?.name}`;
}

// --- legs ---------------------------------------------------------------------------

const legKey = (matchKey, selection) => `${matchKey}:${selection}`;

/** The identity of a multi as a bet: its selections, whatever book it is at. */
export const multiKey = (legs) => legs.map((l) => l.key ?? legKey(l.matchKey, l.side)).sort().join('|');

/**
 * Every leg the request allows, priced at every book that quotes it.
 *
 * Hard filters only — avoid, only, the window, the start, the price range, the
 * punter's own excludes. Preferences are scored later, never filtered here.
 */
export function legsFor(boards, request, { asOf }) {
  const r = normaliseRequest(request);
  const asOfMs = Date.parse(asOf);
  const legs = [];
  const seen = { events: 0, started: 0, outside: 0, avoided: 0 };
  const onlySports = r.sports.filter((s) => s.weight === 'only').map((s) => s.sport);
  const avoidSports = r.sports.filter((s) => s.weight === 'avoid').map((s) => s.sport);
  const onlyEvents = r.events.filter((e) => e.weight === 'only').map((e) => e.event);
  const avoidEvents = new Set(r.events.filter((e) => e.weight === 'avoid').map((e) => e.event));
  const onlyBooks = r.bookmakers.filter((b) => b.weight === 'only' || b.weight === 'must').map((b) => b.bookmaker);
  const avoidBooks = new Set(r.bookmakers.filter((b) => b.weight === 'avoid').map((b) => b.bookmaker));
  const exclude = new Set(r.exclude);
  const labels = new Map();

  for (const board of boards) {
    const universe = board.universe ?? board;
    const [from, to] = windowFor(r.when, { forDate: universe.forDate, asOf });
    for (const match of universe.matches.values()) {
      seen.events += 1;
      labels.set(match.matchKey, eventLabel(match));
      const start = Date.parse(match.kickoff);
      const lead = match.runners ? RACING_LEAD_MS : SPORT_LEAD_MS;
      if (!(start - asOfMs >= lead) || match.inplayAt) { seen.started += 1; continue; }
      if (start < from || start >= to) { seen.outside += 1; continue; }
      if (avoidSports.some((id) => sportMatches(id, match.sport)) || avoidEvents.has(match.matchKey)) {
        seen.avoided += 1;
        continue;
      }
      if (onlySports.length && !onlySports.some((id) => sportMatches(id, match.sport))) continue;
      if (onlyEvents.length && !onlyEvents.includes(match.matchKey)) continue;

      const selections = (match.selections ?? ['home', 'away']).filter((s) => s !== 'draw');
      const perSelection = [];
      for (const selection of selections) {
        const key = legKey(match.matchKey, selection);
        if (exclude.has(key)) continue;
        const market = consensus(match, selection, {})?.pFair ?? null;
        perSelection.push({ selection, key, market });
      }
      // A race offers its likeliest few runners and no more: a $41 chance is not
      // a leg anyone asks a multi builder for, and fourteen of them per race
      // would bury the card's two hundred favourites.
      const offered = match.runners
        ? perSelection.filter((s) => Number.isFinite(s.market))
          .sort((a, b) => b.market - a.market || (a.key < b.key ? -1 : 1))
          .slice(0, RUNNERS_PER_RACE)
        : perSelection;

      for (const { selection, key } of offered) {
        const named = describe(match, selection);
        for (const row of match.prices?.[selection] ?? []) {
          const book = row.bookmaker;
          if (NOT_A_BET_BOOK.includes(book) || avoidBooks.has(book)) continue;
          if (onlyBooks.length && !onlyBooks.includes(book)) continue;
          const price = row.price;
          if (!(price >= PRICE_RANGE[0] && price <= PRICE_RANGE[1])) continue;
          // The market's chance with THIS book's vote left out; where nobody
          // else prices it, the book's own price with its margin off.
          const view = consensus(match, selection, { exclude: book });
          const pBook = bookFair(match, selection, book);
          const pTrue = view?.pFair ?? pBook;
          if (!Number.isFinite(pTrue) || pTrue <= 0 || pTrue >= 1) continue;
          const home = match.home?.id ? teamId(match.sport, match.home.name) ?? match.home.id : null;
          const away = match.away?.id ? teamId(match.sport, match.away.name) ?? match.away.id : null;
          legs.push({
            key,
            matchKey: match.matchKey,
            providerEventId: match.providerEventId ?? null,
            event: labels.get(match.matchKey),
            sport: match.sport,
            sportTitle: match.sportTitle ?? match.competition ?? sportLabel(match.sport),
            kickoff: match.kickoff,
            selection,
            team: named.team,
            number: named.number,
            match: named.match,
            teams: [home, away].filter(Boolean).map((t) => `${match.sport}:${t}`),
            bookmaker: book,
            bookmakerName: row.bookmakerName ?? book,
            ...legLink(match, selection, book),
            price,
            pTrue,
            pBook: Number.isFinite(pBook) ? pBook : null,
            books: view?.books ?? 1,
            ev: pTrue * price - 1,
            capturedAt: row.capturedAt ?? null,
            // What a search reads: the side, the game, the venue, the sport.
            hay: [named.team, named.match, labels.get(match.matchKey), match.venue,
              sportLabel(match.sport), match.sportTitle, match.competition]
              .filter((x) => typeof x === 'string' && x).join('\n').toLowerCase(),
          });
        }
      }
    }
  }
  legs.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : a.bookmaker < b.bookmaker ? -1 : a.bookmaker > b.bookmaker ? 1 : 0));
  return { legs, seen, labels };
}

// --- scoring ------------------------------------------------------------------------

function prefersOf(r) {
  return {
    sports: r.sports.filter((s) => s.weight === 'prefer').map((s) => s.sport),
    events: new Set(r.events.filter((e) => e.weight === 'prefer').map((e) => e.event)),
    books: new Set(r.bookmakers.filter((b) => b.weight === 'prefer').map((b) => b.bookmaker)),
  };
}

const isPreferred = (leg, prefers) => prefers.sports.some((id) => sportMatches(id, leg.sport))
  || prefers.events.has(leg.matchKey) || prefers.books.has(leg.bookmaker);

/** One leg's contribution to its multi's score. Additive by construction. */
function legScore(leg, strategy, prefers) {
  const value = Math.log(Math.max(1e-9, 1 + leg.ev));
  const bonus = isPreferred(leg, prefers) ? PREFER_BONUS : 0;
  if (strategy === 'likely') return Math.log(leg.pTrue) + bonus;
  if (strategy === 'longshots') return value + 0.12 * Math.log(leg.price) + bonus;
  return value + bonus;
}

// --- the search ----------------------------------------------------------------------

function bandOf(r) {
  if (r.odds) return { kind: 'odds', lo: r.odds.min, hi: r.odds.max };
  if (r.risk) {
    const [lo, hi] = RISK_BANDS[r.risk];
    return { kind: 'p', lo, hi };
  }
  return null;
}

const inBand = (band, p, price) => !band
  || (band.kind === 'p' ? p >= band.lo && p <= band.hi : price >= band.lo && price <= band.hi);

/**
 * All the multis one book can write that satisfy the hard constraints, best
 * first. A beam over leg sets, depth by depth; free legs are only ever appended
 * in pool order, so a set is reached once.
 */
function searchBook(bookLegs, spec) {
  const { r, strategy, prefers, band, mustEvents, mustSports, include, seed } = spec;
  const search = r.search;
  const scored = bookLegs.map((l) => ({
    ...l, score: legScore(l, strategy, prefers), h: TIE(seed, l.key), hit: !search || l.hay.includes(search),
  }))
    .sort((a, b) => b.score - a.score || a.h - b.h);

  // THE REQUIRED LEGS: every pinned leg, and one leg of each must event. A book
  // that cannot price a pinned leg cannot write this multi at all.
  const pinned = [];
  for (const key of include) {
    const leg = scored.find((l) => l.key === key);
    if (!leg) return [];
    pinned.push(leg);
  }
  const eventGroups = [];
  for (const event of mustEvents) {
    if (pinned.some((l) => l.matchKey === event)) continue;
    const group = scored.filter((l) => l.matchKey === event);
    if (!group.length) return [];
    eventGroups.push(group);
  }
  let seeds = [pinned];
  for (const group of eventGroups) {
    const next = [];
    for (const s of seeds) for (const leg of group) next.push([...s, leg]);
    seeds = next.slice(0, 64);
  }
  // A must SPORT is met by a seed or by a slot the beam must fill: branch over
  // that sport's best few legs so it is never crowded out.
  for (const sport of mustSports) {
    if (seeds.every((s) => s.some((l) => sportMatches(sport, l.sport)))) continue;
    const best = scored.filter((l) => sportMatches(sport, l.sport)).slice(0, 6);
    if (!best.length) return [];
    const next = [];
    for (const s of seeds) {
      if (s.some((l) => sportMatches(sport, l.sport))) { next.push(s); continue; }
      for (const leg of best) if (!clashes(s, leg)) next.push([...s, leg]);
    }
    seeds = next.slice(0, 64);
  }
  // A SEARCH is a must over the legs that say it: the same branching, over the
  // best few matching legs, so the one Brisbane leg is never crowded out.
  if (search && !seeds.every((s) => s.some((l) => l.hit))) {
    const best = scored.filter((l) => l.hit).slice(0, 6);
    if (!best.length) return [];
    const next = [];
    for (const s of seeds) {
      if (s.some((l) => l.hit)) { next.push(s); continue; }
      for (const leg of best) if (!clashes(s, leg)) next.push([...s, leg]);
    }
    seeds = next.slice(0, 64);
  }

  // THE POOL the beam draws from: the best legs by score AND the likeliest legs,
  // because a low- or medium-risk band is made of favourites and a pool of
  // value long shots has none of them left in it.
  const pool = scored.slice(0, POOL);
  const likeliest = scored.slice().sort((a, b) => b.pTrue - a.pTrue || a.h - b.h);
  for (const l of likeliest.slice(0, POOL / 2)) if (!pool.includes(l)) pool.push(l);
  for (const s of seeds) for (const l of s) if (!pool.includes(l)) pool.push(l);

  // `h` is an order-free hash of the leg set — the tie-break, computed as the
  // state grows rather than by building the set's key inside a comparator.
  const state = (legs, last) => {
    let p = 1;
    let price = 1;
    let score = 0;
    let h = 0;
    for (const l of legs) { p *= l.pTrue; price *= l.price; score += l.score; h = (h + l.h) >>> 0; }
    return { legs, last, p, price, score, h };
  };
  let frontier = seeds.filter((s) => s.length <= r.legs.max && !selfClash(s)).map((s) => state(s, -1));
  // THE BEST `keep`, NOT EVERYTHING. A board makes tens of thousands of multis
  // and a page shows a handful, so a multi is identified by a number (its
  // order-free leg hash and its length) until it is good enough to be kept;
  // only the kept ones are ever given a text key. Every distinct one found is
  // still counted, in `spec.found`, which is what "12,000 multis match" reads.
  const keep = spec.keep ?? KEEP;
  const heap = [];
  let recorded = 0;
  const done = new Set();
  const logLo = band?.kind === 'p' ? Math.log(band.lo) : null;
  const logHi = band?.kind === 'p' ? Math.log(Math.min(band.hi, 0.999)) : null;

  const record = (s) => {
    if (s.legs.length < r.legs.min || s.legs.length > r.legs.max) return;
    if (!inBand(band, s.p, s.price)) return;
    if (!mustSports.every((sp) => s.legs.some((l) => sportMatches(sp, l.sport)))) return;
    if (search && !s.legs.some((l) => l.hit)) return;
    const id = s.h * 16 + s.legs.length;
    if (done.has(id)) return;
    done.add(id);
    recorded += 1;
    if (spec.excludedIds?.has(id)) return;
    spec.found?.add(id);
    if (heap.length < keep) heapPush(heap, s);
    else if (s.score > heap[0].score) { heap[0] = s; siftDown(heap, 0); }
  };
  frontier.forEach(record);

  for (let depth = 0; depth < r.legs.max && frontier.length && recorded < COMPLETES_PER_BOOK; depth++) {
    const next = [];
    for (const s of frontier) {
      if (s.legs.length >= r.legs.max) continue;
      for (let j = s.last + 1; j < pool.length; j++) {
        const leg = pool[j];
        if (s.legs.includes(leg) || clashes(s.legs, leg)) continue;
        const grown = state([...s.legs, leg], j);
        // A chance already under the band's floor only falls from here.
        if (band?.kind === 'p' && grown.p < band.lo) continue;
        if (band?.kind === 'odds' && grown.price > band.hi) continue;
        next.push(grown);
      }
    }
    next.forEach(record);
    // Keep the best by score, and — so a band far from the favourites is still
    // reachable — the best by how close their chance is to the band's middle.
    const byScore = next.slice().sort((a, b) => b.score - a.score || a.h - b.h);
    const beam = byScore.slice(0, band ? BEAM : BEAM_FREE);
    if (band && logLo !== null) {
      // Judged against where a ticket on its way to the band's middle should be
      // at this depth, not against the finish: a one-leg state at the final
      // target is a state with nowhere left to go.
      const legsTarget = (r.legs.min + r.legs.max) / 2;
      const along = (st) => Math.abs(Math.log(st.p) - ((logLo + logHi) / 2) * Math.min(1, st.legs.length / legsTarget));
      const byBand = next.slice().sort((a, b) => along(a) - along(b)
        || b.score - a.score || a.h - b.h);
      for (const st of byBand.slice(0, BEAM_BAND)) if (!beam.includes(st)) beam.push(st);
    }
    frontier = beam;
  }
  for (const c of heap) c.key = multiKey(c.legs);
  return heap;
}

// A min-heap on score: the root is the weakest kept multi, the one a better
// one replaces.
function heapPush(heap, item) {
  heap.push(item);
  let i = heap.length - 1;
  while (i > 0) {
    const up = (i - 1) >> 1;
    if (heap[up].score <= item.score) break;
    heap[i] = heap[up];
    i = up;
  }
  heap[i] = item;
}
function siftDown(heap, i) {
  const item = heap[i];
  const n = heap.length;
  for (;;) {
    let c = 2 * i + 1;
    if (c >= n) break;
    if (c + 1 < n && heap[c + 1].score < heap[c].score) c += 1;
    if (heap[c].score >= item.score) break;
    heap[i] = heap[c];
    i = c;
  }
  heap[i] = item;
}


/** A multi's numeric id from its key: the order-free sum of its legs' hashes, and its length. */
function idOf(seed, key) {
  const legKeys = String(key).split('|');
  let h = 0;
  for (const k of legKeys) h = (h + TIE(seed, k)) >>> 0;
  return h * 16 + legKeys.length;
}

/** Two legs that are one bet: the same event, or the same team on two events. */
function clashes(legs, leg) {
  return legs.some((l) => l.matchKey === leg.matchKey || l.teams.some((t) => leg.teams.includes(t)));
}
const selfClash = (legs) => legs.some((l, i) => clashes(legs.slice(0, i), l));

// --- the answer ----------------------------------------------------------------------

const round2 = (n) => Math.round(n * 100) / 100;

function toTicket(s, { r, forDate, oddsFetchedAt, stake, prefers, allLegs, seed, relaxed, labels }) {
  const n = s.legs.length;
  const legs = s.legs.slice().sort((a, b) => a.kickoff.localeCompare(b.kickoff) || a.key.localeCompare(b.key));
  const key = multiKey(legs);
  const id = `ask-${forDate}-${fnv1a(key).toString(16)}`;
  const combinedOdds = round2(legs.reduce((acc, l) => acc * l.price, 1));
  const p = legs.reduce((acc, l) => acc * l.pTrue, 1);
  const pBook = legs.every((l) => Number.isFinite(l.pBook)) ? legs.reduce((acc, l) => acc * l.pBook, 1) : null;
  // The value the ticket is RANKED and PRINTED on, with the estimation-error
  // haircut every other multi in the app takes (combine.js, engine review #10).
  const pUsed = legs.reduce((acc, l) => acc * (Number.isFinite(l.pBook) ? shrinkToward(l.pTrue, l.pBook, n) : l.pTrue), 1);
  const ev = pUsed * combinedOdds - 1;
  const book = legs[0];
  return {
    multi: {
      id,
      askKey: key,
      forDate,
      status: 'proposed',
      stake,
      bookmaker: book.bookmakerName,
      bookmakerKey: book.bookmaker,
      oddsFetchedAt,
      combinedOdds,
      // THE REAL CHANCE: the market's, margin off, legs taken as independent.
      combinedProbability: p,
      modelProbability: p,
      fairProbability: pBook,
      potentialReturn: round2(stake * combinedOdds),
      ev,
      edge: Number.isFinite(pBook) ? p - pBook : null,
      tier: tierOf(p),
      strategyId: ASK_STRATEGY_ID,
      strategyVersion: ASK_VERSION,
      label: 'Your multi',
      score: s.score,
      honoured: honoured(legs, r, prefers, allLegs, labels),
      relaxed,
      seed,
    },
    legs: legs.map((l) => ({
      id: `${id}-${l.key}`,
      multiId: id,
      key: l.key,
      eventId: l.providerEventId,
      providerEventId: l.providerEventId,
      matchKey: l.matchKey,
      event: l.event,
      selection: `${l.number ? `${l.team} (${l.number})` : l.team} to win`,
      match: l.match,
      sportTitle: l.sportTitle,
      sport: l.sport,
      commenceTime: l.kickoff,
      price: l.price,
      impliedProbability: 1 / l.price,
      side: l.selection,
      bookmakerKey: l.bookmaker,
      modelProbability: l.pTrue,
      fairProbability: l.pBook,
      edge: Number.isFinite(l.pBook) ? l.pTrue - l.pBook : null,
      ev: l.ev,
      books: l.books,
      rationale: rationale(l, prefers, r.include.includes(l.key)),
      locked: r.include.includes(l.key),
      ...(l.link ? { link: l.link, linkKind: l.linkKind } : {}),
    })),
  };
}

function tierOf(p) {
  for (const risk of RISKS) {
    const [lo, hi] = RISK_BANDS[risk];
    if (p >= lo && p < (risk === 'low' ? Infinity : hi)) return risk;
  }
  return 'outrageous';
}

const pct = (p) => `${Math.round(p * 100)}%`;

/** One line, per leg, saying why it is there. */
export function rationale(leg, prefers = null, locked = false) {
  const said = locked ? ['your pick'] : [];
  const fair = 1 / leg.pTrue;
  if (leg.pTrue >= 0.6) said.push(`favourite, ${pct(leg.pTrue)} to win`);
  else said.push(`${pct(leg.pTrue)} to win`);
  if (leg.books > 1) {
    const gap = leg.ev;
    if (gap >= 0.02) said.push(`$${leg.price.toFixed(2)} beats the market's fair $${fair.toFixed(2)} (+${(gap * 100).toFixed(1)}%)`);
    else if (gap > -0.02) said.push(`$${leg.price.toFixed(2)} is about the market's fair $${fair.toFixed(2)}`);
    else said.push(`$${leg.price.toFixed(2)} is under the market's fair $${fair.toFixed(2)} (${(gap * 100).toFixed(1)}%)`);
  } else said.push(`only ${leg.bookmakerName} prices it`);
  if (prefers && isPreferred(leg, prefers)) said.push('you asked for this');
  return said.join(' · ');
}

/**
 * How each preference was honoured, in words — and when it was not, why not.
 * Must, only and avoid are reported too, because "you asked for it and it is
 * there" is the sentence that makes the ticket trustworthy.
 */
function honoured(legs, r, prefers, allLegs, labels) {
  const out = [];
  const n = legs.length;
  for (const s of r.sports) {
    const name = sportName(s.sport);
    const count = legs.filter((l) => sportMatches(s.sport, l.sport)).length;
    if (s.weight === 'prefer') {
      const used = new Set(legs.map((l) => l.matchKey));
      const book = legs[0].bookmaker;
      const spare = allLegs.filter((l) => l.bookmaker === book && sportMatches(s.sport, l.sport) && !used.has(l.matchKey));
      const spareEvents = new Set(spare.map((l) => l.matchKey)).size;
      let why = '';
      if (count < n) {
        if (!spareEvents) why = count ? `: no other ${name} legs left at ${legs[0].bookmakerName}` : `: no ${name} legs left at ${legs[0].bookmakerName}`;
        else {
          why = `: ${whyNotMore(legs, spare, s.sport, r)}`;
        }
      }
      out.push({ dimension: 'sport', value: s.sport, weight: 'prefer', met: count > 0, text: `${count} of ${n} legs ${name}${why}` });
    } else if (s.weight === 'avoid') {
      out.push({ dimension: 'sport', value: s.sport, weight: 'avoid', met: count === 0, text: `no ${name}` });
    } else if (s.weight === 'only') {
      out.push({ dimension: 'sport', value: s.sport, weight: 'only', met: count === n, text: n === 1 ? `the leg is ${name}` : `all ${n} legs ${name}` });
    } else {
      out.push({ dimension: 'sport', value: s.sport, weight: 'must', met: count > 0, text: `${count} ${name} leg${count === 1 ? '' : 's'}` });
    }
  }
  for (const e of r.events) {
    const name = labels.get(e.event) ?? e.event;
    const has = legs.some((l) => l.matchKey === e.event);
    const text = e.weight === 'avoid' ? `no ${name}`
      : e.weight === 'prefer' ? (has ? `includes ${name}` : `${name} left out: it made a weaker multi`)
        : `includes ${name}`;
    out.push({ dimension: 'event', value: e.event, weight: e.weight, met: e.weight === 'avoid' ? !has : has || e.weight === 'prefer', text });
  }
  if (prefers.books.size) {
    const at = legs[0].bookmaker;
    out.push({
      dimension: 'bookmaker', value: at, weight: 'prefer', met: prefers.books.has(at),
      text: prefers.books.has(at) ? `at ${legs[0].bookmakerName}, as preferred` : `at ${legs[0].bookmakerName}: it wrote a better ticket`,
    });
  }
  return out;
}

/**
 * Why a preferred sport has fewer legs than it could: try putting its best
 * spare leg in place of each leg that is not that sport, and say what stopped it.
 */
function whyNotMore(legs, spare, sport, r) {
  const name = sportName(sport);
  const band = bandOf(r);
  const others = legs.filter((l) => !sportMatches(sport, l.sport));
  let clash = 0;
  let outOfBand = 0;
  for (const cand of spare) {
    for (const out of others) {
      const swapped = legs.filter((l) => l !== out);
      if (clashes(swapped, cand)) { clash += 1; continue; }
      const p = swapped.reduce((acc, l) => acc * l.pTrue, cand.pTrue);
      const price = swapped.reduce((acc, l) => acc * l.price, cand.price);
      if (!inBand(band, p, price)) { outOfBand += 1; continue; }
      return `the other ${name} legs are worse value than the ones picked`;
    }
  }
  if (outOfBand) return `the other ${name} legs would take it out of the ${r.risk ? `${r.risk}-risk band` : 'price range'}`;
  if (clash) return `the other ${name} legs clash with legs already on it`;
  return `the other ${name} legs scored lower`;
}

/** In plain words, what the punter asked for. The results header. */
export function interpret(request, labels = new Map()) {
  const r = normaliseRequest(request);
  const eventLabels = new Map(r.events.filter((e) => e.label).map((e) => [e.event, e.label]));
  const name = (e) => labels.get(e) ?? eventLabels.get(e) ?? e;
  const legs = r.legs.min === r.legs.max ? `${r.legs.min}-leg ` : '';
  const risk = r.risk ? `${r.risk === 'outrageous' ? 'Outrageous' : `${cap(r.risk)}-risk`} ` : '';
  const head = `${risk}${legs}${r.legs.max === 1 ? 'singles' : 'multis'}`;
  const bits = [];
  const mustEvents = r.events.filter((e) => e.weight === 'must').map((e) => name(e.event));
  if (mustEvents.length) bits.push(`that include ${list(mustEvents)}`);
  const onlyEvents = r.events.filter((e) => e.weight === 'only').map((e) => name(e.event));
  if (onlyEvents.length) bits.push(`only on ${list(onlyEvents)}`);
  const only = r.sports.filter((s) => s.weight === 'only').map((s) => sportName(s.sport));
  if (only.length) bits.push(`${list(only)} only`);
  const must = r.sports.filter((s) => s.weight === 'must').map((s) => sportName(s.sport));
  if (must.length) bits.push(`with at least one ${list(must, 'and')} leg`);
  const prefer = [...r.sports.filter((s) => s.weight === 'prefer').map((s) => sportName(s.sport)),
    ...r.events.filter((e) => e.weight === 'prefer').map((e) => name(e.event))];
  if (prefer.length) bits.push(`leaning ${list(prefer)}`);
  const avoid = [...r.sports.filter((s) => s.weight === 'avoid').map((s) => sportName(s.sport)),
    ...r.events.filter((e) => e.weight === 'avoid').map((e) => name(e.event))];
  if (avoid.length) bits.push(`no ${list(avoid)}`);
  if (r.odds) bits.push(payingPhrase(r.odds));
  if (r.search) bits.push(`matching “${r.search}”`);
  if (r.include.length) bits.push(`built around ${r.include.length} leg${r.include.length === 1 ? '' : 's'} you picked`);
  const tail = {
    best: 'best value', edge: 'value legs only', likely: 'likeliest', longshots: 'longer prices',
  }[r.strategy];
  const when = whenPhrase(r.when);
  const card = when === 'today' ? "today's card" : `the card ${when}`;
  return `${cap(head)}${bits.length ? ` ${bits.join(', ')}` : ''} · ${tail} on ${card}`;
}
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const dollars = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
/** "paying $3–$10", "paying $50+", "paying under $3": an odds range, in words. */
export function payingPhrase(odds) {
  const lo = Number.isFinite(odds?.min) && odds.min > 1 ? odds.min : null;
  const hi = Number.isFinite(odds?.max) ? odds.max : null;
  if (lo && hi) return `paying ${dollars(lo)}–${dollars(hi)}`;
  if (lo) return `paying ${dollars(lo)}+`;
  if (hi) return `paying under ${dollars(hi)}`;
  return 'at any price';
}
function list(items, and = 'or') {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

/** Pick `count` from the ranked list: nothing shown before, nothing near-identical. */
function pickDistinct(ranked, count, { excluded, fixed }) {
  const picked = [];
  const overlap = (a, b) => a.legs.filter((l) => !fixed.has(l.key) && b.legs.some((x) => x.key === l.key)).length;
  const free = (s) => s.legs.filter((l) => !fixed.has(l.key)).length;
  for (const strict of [true, false]) {
    for (const s of ranked) {
      if (picked.length >= count) break;
      const key = s.key;
      if (excluded.has(key) || picked.some((p) => p.key === key)) continue;
      // Strict: at least two legs differ from every ticket already picked (one,
      // on a ticket with only one or two free legs). Loose: any difference.
      const need = strict ? Math.min(2, Math.max(1, free(s))) : 1;
      if (picked.some((p) => free(s) - overlap(s, p) < need)) continue;
      // Strict, too: no free leg on more than half the tickets shown. One
      // outlier price should not be the answer to every question.
      if (strict && s.legs.some((l) => !fixed.has(l.key)
        && picked.filter((p) => p.legs.some((x) => x.key === l.key)).length >= Math.max(1, Math.floor(count / 2)))) continue;
      picked.push(s);
    }
  }
  return picked;
}

/**
 * The answer to a request.
 *
 * boards: `[{ universe, kind, fetchedAt }]` — the sports board and the racing
 *   card as the device holds them (`buildUniverse`), or bare universes.
 * opts: { asOf (ISO, required), count = 3, stake = 10 }
 *
 * Returns { request, interpretation, results: [{ multi, legs }], unsatisfied,
 * relaxed, freshness, stats }. `results` is empty only when even the loosened
 * request has nothing; `unsatisfied` then says what to change.
 */
export function answer(boards, request, opts = {}) {
  const asOf = opts.asOf;
  if (!asOf) throw new Error('answer: asOf is required');
  const count = Math.max(1, Math.min(10, opts.count ?? 3));
  const stake = Number.isFinite(opts.stake) ? opts.stake : 10;
  const r = normaliseRequest(request);
  const list = (Array.isArray(boards) ? boards : [boards]).filter(Boolean)
    .map((b) => (b.universe ? b : { universe: b, kind: null, fetchedAt: b.asOf ?? null }));
  const forDate = list[0]?.universe?.forDate ?? String(asOf).slice(0, 10);
  const asOfMs = Date.parse(asOf);
  const freshness = list.map((b) => ({
    kind: b.kind ?? null,
    fetchedAt: b.fetchedAt ?? null,
    ageMinutes: b.fetchedAt ? Math.max(0, Math.round((asOfMs - Date.parse(b.fetchedAt)) / 60_000)) : null,
  }));
  const oddsFetchedAt = freshness.map((f) => f.fetchedAt).filter(Boolean).sort()[0] ?? asOf;

  // The legs depend only on the hard filters; a relaxation of the leg count or
  // the risk band reuses them.
  const legCache = new Map();
  const legsOf = (req) => {
    const k = JSON.stringify([req.sports, req.events, req.bookmakers, req.when, req.exclude]);
    if (!legCache.has(k)) legCache.set(k, legsFor(list, req, { asOf }));
    return legCache.get(k);
  };
  const attempt = (req, relaxed) => {
    const { legs, seen, labels } = legsOf(req);
    const spec = {
      r: req,
      strategy: req.strategy,
      prefers: prefersOf(req),
      band: bandOf(req),
      mustEvents: req.events.filter((e) => e.weight === 'must').map((e) => e.event),
      mustSports: req.sports.filter((s) => s.weight === 'must').map((s) => s.sport),
      include: req.include,
      seed: req.seed,
      // The multis already shown, by the same numeric id the search uses, so
      // "another" skips them without building a key for anything it passes.
      excludedIds: new Set(req.excludeMultis.map((k) => idOf(req.seed, k))),
      found: new Set(),
      keep: Math.max(KEEP, count * 12),
    };
    const pool = req.strategy === 'edge' ? legs.filter((l) => l.ev >= 0.02 || req.include.includes(l.key)) : legs;
    const books = booksToSearch(pool, req);
    let completes = [];
    for (const book of books) completes = completes.concat(searchBook(pool.filter((l) => l.bookmaker === book), spec));
    // One bet at its best book: the same selections at a worse price is not
    // another multi, it is the same multi somewhere worse.
    const best = new Map();
    const valueOf = (s) => {
      const n = s.legs.length;
      const pUsed = s.legs.reduce((acc, l) => acc * (Number.isFinite(l.pBook) ? shrinkToward(l.pTrue, l.pBook, n) : l.pTrue), 1);
      return pUsed * s.price - 1;
    };
    for (const s of completes) {
      const prev = best.get(s.key);
      if (!prev || s.score > prev.score + 1e-12 || (Math.abs(s.score - prev.score) <= 1e-12 && s.legs[0].bookmaker < prev.legs[0].bookmaker)) best.set(s.key, s);
    }
    for (const s of best.values()) s.value = valueOf(s);
    const ranked = [...best.values()].sort((a, b) => b.score - a.score || b.value - a.value || a.h - b.h
      || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const picked = pickDistinct(ranked, count, { excluded: new Set(req.excludeMultis), fixed: new Set(req.include) });
    const results = picked.map((s) => toTicket(s, {
      r: req, forDate, oddsFetchedAt, stake, prefers: spec.prefers, allLegs: legs, seed: req.seed, relaxed, labels,
    }));
    return { results, legs, seen, labels, distinct: spec.found.size, considered: completes.length };
  };

  const first = attempt(r, []);
  const stats = {
    legs: first.legs.length,
    events: new Set(first.legs.map((l) => l.matchKey)).size,
    considered: first.considered,
    distinct: first.distinct,
    // Every distinct multi that fits, shown or not: the list's length.
    matching: first.distinct + r.excludeMultis.length,
    shownBefore: r.excludeMultis.length,
  };
  if (first.results.length) {
    return {
      request: r, interpretation: interpret(r, first.labels), results: first.results,
      unsatisfied: null, relaxed: [], freshness, stats,
    };
  }

  // NOTHING FITS: say why, then try the loosenings that never touch a must, an
  // only or an avoid — the leg count, the risk band, the window.
  const why = diagnose(r, first, list, asOf, legsOf);
  const tries = [];
  const events = new Set(first.legs.map((l) => l.matchKey)).size;
  if (events >= 1 && events < r.legs.min) {
    tries.push({ change: { legs: { min: events, max: events } }, relaxed: [{ dimension: 'legs', from: r.legs, to: events, why: `only ${events} event${events === 1 ? '' : 's'} fit` }] });
  }
  if (r.risk || r.odds) {
    tries.push({ change: { risk: null, odds: null }, relaxed: [{ dimension: 'risk', from: r.risk ?? r.odds, to: null, why: 'nothing on the card lands in that band' }] });
    if (events >= 1 && events < r.legs.min) {
      tries.push({
        change: { risk: null, odds: null, legs: { min: events, max: events } },
        relaxed: [{ dimension: 'legs', from: r.legs, to: events, why: `only ${events} event${events === 1 ? '' : 's'} fit` },
          { dimension: 'risk', from: r.risk ?? r.odds, to: null, why: 'nothing lands in that band' }],
      });
    }
  }
  if (r.when !== 'today' && typeof r.when === 'string' && r.when !== 'tomorrow') {
    tries.push({ change: { when: 'today' }, relaxed: [{ dimension: 'when', from: r.when, to: 'today', why: `nothing fits ${whenPhrase(r.when)}` }] });
  }
  for (const t of tries) {
    const req = normaliseRequest({ ...r, ...t.change });
    const got = attempt(req, t.relaxed);
    if (got.results.length) {
      return {
        request: r, interpretation: interpret(r, first.labels), results: got.results,
        unsatisfied: why, relaxed: t.relaxed, freshness, stats,
      };
    }
  }
  return {
    request: r, interpretation: interpret(r, first.labels), results: [], unsatisfied: why, relaxed: [], freshness, stats,
  };
}

/**
 * The books worth searching: the ones that price the most events, plus every
 * book the request names. A shop that quotes a tenth of the card cannot write
 * the best multi on it, and searching it costs as much as searching one that can.
 */
export const BOOKS_SEARCHED = 6;
function booksToSearch(pool, req) {
  const events = new Map();
  for (const l of pool) {
    if (!events.has(l.bookmaker)) events.set(l.bookmaker, new Set());
    events.get(l.bookmaker).add(l.matchKey);
  }
  const named = new Set(req.bookmakers.filter((b) => b.weight !== 'avoid').map((b) => b.bookmaker));
  const ranked = [...events.entries()].sort((a, b) => b[1].size - a[1].size || (a[0] < b[0] ? -1 : 1)).map(([k]) => k);
  return [...new Set([...ranked.slice(0, BOOKS_SEARCHED), ...ranked.filter((k) => named.has(k))])].sort();
}

/**
 * Why a request has no answer, as sentences, and the loosenings that would give
 * it one. Never "nothing found": always a number and a thing to change.
 */
function diagnose(r, got, boards, asOf, legsOf) {
  const reasons = [];
  const loosen = [];
  const all = legsOf(normaliseRequest({ ...r, sports: r.sports.filter((x) => x.weight === 'avoid'), events: [], include: [], exclude: [] }));
  const labels = got.labels;
  const asOfMs = Date.parse(asOf);

  // A SEARCH NOTHING SAYS: the first thing to change, and the plainest.
  if (r.search && !got.legs.some((l) => l.hay.includes(r.search))) {
    reasons.push(`nothing on the card ${whenPhrase(r.when)} matches “${r.search}”`);
    loosen.push({ dimension: 'search', suggestion: 'clear the search' });
  }

  for (const e of r.events.filter((x) => x.weight === 'must' || x.weight === 'only')) {
    const name = labels.get(e.event) ?? e.label ?? 'that event';
    if (!got.legs.some((l) => l.matchKey === e.event)) {
      let where = null;
      for (const b of boards) where = where ?? (b.universe ?? b).matches.get(e.event);
      if (!where && e.kickoff && Date.parse(e.kickoff) - asOfMs < SPORT_LEAD_MS) {
        reasons.push(`${name} has started`);
        loosen.push({ dimension: 'event', value: e.event, suggestion: `drop ${name}` });
        continue;
      }
      const avoidedBy = where && r.sports.find((x) => x.weight === 'avoid' && sportMatches(x.sport, where.sport));
      const notOnly = where && r.sports.some((x) => x.weight === 'only')
        && !r.sports.some((x) => x.weight === 'only' && sportMatches(x.sport, where.sport));
      const why = !where ? `${name} is not on the board`
        : Date.parse(where.kickoff) - asOfMs < (where.runners ? RACING_LEAD_MS : SPORT_LEAD_MS) ? `${name} has started`
          : avoidedBy ? `${name} is ${sportName(where.sport)}, and you asked for no ${sportName(avoidedBy.sport)}`
            : notOnly ? `${name} is not in the only sport you asked for`
              : `${name} is outside ${whenPhrase(r.when)}`;
      reasons.push(why);
      loosen.push({ dimension: 'event', value: e.event, suggestion: `drop ${name}` });
    }
  }
  // A LOCKED LEG THAT CAN NO LONGER BE TAKEN: started, scratched, or priced by
  // no book the rest of the multi could be written at.
  for (const key of r.include) {
    if (got.legs.some((l) => l.key === key)) continue;
    const [matchKey] = [key.slice(0, key.lastIndexOf(':'))];
    let where = null;
    for (const b of boards) where = where ?? (b.universe ?? b).matches.get(matchKey);
    const side = key.slice(key.lastIndexOf(':') + 1);
    const name = where ? (describe(where, side).team ?? 'a leg you locked') : 'a leg you locked';
    reasons.push(!where ? `${name} is no longer on the board (it has started or closed)`
      : `${name} can't go in this multi as asked`);
    loosen.push({ dimension: 'include', value: key, suggestion: `unlock ${name}` });
  }
  if (r.include.length && !reasons.length && got.legs.length) {
    const books = new Set(r.include.map((k) => new Set(got.legs.filter((l) => l.key === k).map((l) => l.bookmaker))));
    const common = [...books].reduce((acc, set) => (acc ? new Set([...acc].filter((b) => set.has(b))) : set), null);
    if (common && common.size === 0) {
      reasons.push('no single bookmaker prices every leg you locked');
      loosen.push({ dimension: 'include', value: r.include[r.include.length - 1], suggestion: 'unlock the last leg you picked' });
    }
  }
  // THE ONE BOOK ASKED FOR IS NOT PRICING ENOUGH: say which book and how much
  // it is pricing, and offer every book.
  const onlyBooks = r.bookmakers.filter((b) => b.weight === 'only' || b.weight === 'must').map((b) => b.bookmaker);
  if (onlyBooks.length && !reasons.length) {
    const events = new Set(got.legs.map((l) => l.matchKey)).size;
    if (events < r.legs.min) {
      const name = got.legs[0]?.bookmakerName ?? onlyBooks[0];
      reasons.push(events === 0
        ? `${name} has no prices on the board ${whenPhrase(r.when)}`
        : `${name} is only pricing ${events} event${events === 1 ? '' : 's'} ${whenPhrase(r.when)}`);
      loosen.push({ dimension: 'bookmaker', suggestion: 'any bookmaker' });
    }
  }
  const eventsIn = (pred) => new Set(all.legs.filter(pred).map((l) => l.matchKey)).size;
  for (const s of r.sports.filter((x) => x.weight === 'only')) {
    const n = eventsIn((l) => sportMatches(s.sport, l.sport));
    const name = sportName(s.sport);
    if (n < r.legs.min) {
      reasons.push(n === 0
        ? `no ${name} left ${whenPhrase(r.when)}`
        : `only ${n} ${name} ${n === 1 ? 'event' : 'events'} left ${whenPhrase(r.when)}, so at most ${n} uncorrelated ${name} leg${n === 1 ? '' : 's'}`);
      if (n >= 1) loosen.push({ dimension: 'legs', suggestion: `${n} leg${n === 1 ? '' : 's'}` });
      loosen.push({ dimension: 'sport', value: s.sport, suggestion: `prefer ${name} instead of ${name} only` });
    }
  }
  for (const s of r.sports.filter((x) => x.weight === 'must')) {
    if (!eventsIn((l) => sportMatches(s.sport, l.sport))) {
      reasons.push(`no ${sportName(s.sport)} left ${whenPhrase(r.when)}`);
      loosen.push({ dimension: 'sport', value: s.sport, suggestion: `drop ${sportName(s.sport)}` });
    }
  }
  const events = new Set(got.legs.map((l) => l.matchKey)).size;
  if (!reasons.length && events < r.legs.min) {
    reasons.push(`only ${events} event${events === 1 ? '' : 's'} fit ${whenPhrase(r.when)}, and ${r.legs.min} legs need ${r.legs.min}`);
    if (events >= 1) loosen.push({ dimension: 'legs', suggestion: `${events} leg${events === 1 ? '' : 's'}` });
    if (r.when !== 'today') loosen.push({ dimension: 'when', suggestion: 'today' });
  }
  if (!reasons.length && (r.risk || r.odds)) {
    reasons.push(`no ${r.legs.min === r.legs.max ? r.legs.min : `${r.legs.min}–${r.legs.max}`}-leg multi ${whenPhrase(r.when)} lands in ${r.risk ? `the ${r.risk}-risk band` : 'that price range'}`);
    loosen.push({ dimension: 'risk', suggestion: 'any risk' });
    loosen.push({ dimension: 'legs', suggestion: 'a different leg count' });
  }
  if (!reasons.length && r.strategy === 'edge') {
    reasons.push('no leg on the card beats the market by 2% or more');
    loosen.push({ dimension: 'strategy', suggestion: 'best value' });
  }
  if (!reasons.length && r.excludeMultis.length) {
    reasons.push(`you have seen every distinct multi this request makes (${r.excludeMultis.length})`);
    loosen.push({ dimension: 'legs', suggestion: 'a different leg count' });
    loosen.push({ dimension: 'sport', suggestion: 'another sport' });
  }
  if (!reasons.length) {
    reasons.push(`nothing ${whenPhrase(r.when)} fits every part of this request`);
    loosen.push({ dimension: 'legs', suggestion: 'fewer legs' });
  }
  return { reasons, loosen };
}
